import { useSyncExternalStore } from 'react';

/**
 * Seen-state for the in-app onboarding tutorial (intro dialog + per-node hints).
 *
 * LocalStorage-backed, framework-agnostic, mirrors the whats-new seen-store
 * pattern (try/catch-guarded storage access + useSyncExternalStore). Marking
 * seen is one-way within a session — nothing here un-marks a node except
 * `resetOnboarding`. Client-only on purpose — no server schema, no API.
 */
const STORAGE_KEY = 'ct-onboarding-state';

/** Delay after intro closes before explore soft-halos unlock. */
export const TOUR_LIGHTS_DELAY_MS = 4000;

/** The persisted (and live) onboarding state shape. */
export interface OnboardingHintState {
  introSeen: boolean;
  seenNodeIds: string[];
  /** Session-only — explore halos stay off until this flips true. */
  tourLightsUnlocked: boolean;
  /**
   * Session-only — the intro modal was closed with its X (or Esc / backdrop), which hides it for
   * this session but is never persisted, so a reload brings the intro back.
   */
  introDismissed: boolean;
  /** Session-only — vivid Send pulse after Get started prefill path. */
  sendNudgeActive: boolean;
  /** Session-only — pathname where the Send nudge is valid; leave that route to clear it. */
  sendNudgePath: string | null;
  /** Session-only — the hint id whose dialog a beacon target opened, or null if none is open. */
  activeDialogHintId: string | null;
}

type PersistedOnboardingState = Pick<
  OnboardingHintState,
  'introSeen' | 'seenNodeIds'
>;

function defaultState(): OnboardingHintState {
  return {
    introSeen: false,
    seenNodeIds: [],
    tourLightsUnlocked: false,
    introDismissed: false,
    sendNudgeActive: false,
    sendNudgePath: null,
    activeDialogHintId: null,
  };
}

function defaultPersisted(): PersistedOnboardingState {
  return { introSeen: false, seenNodeIds: [] };
}

/** A parsed value only counts if every field matches the expected shape — anything else reads as "nothing seen". */
function isValidStoredState(value: unknown): value is PersistedOnboardingState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['introSeen'] === 'boolean' &&
    Array.isArray(candidate['seenNodeIds']) &&
    candidate['seenNodeIds'].every((id) => typeof id === 'string')
  );
}

function hydrateFromPersisted(
  persisted: PersistedOnboardingState,
): OnboardingHintState {
  return {
    introSeen: persisted.introSeen,
    seenNodeIds: persisted.seenNodeIds,
    tourLightsUnlocked: persisted.introSeen,
    introDismissed: false,
    sendNudgeActive: false,
    sendNudgePath: null,
    activeDialogHintId: null,
  };
}

function readFromStorage(): OnboardingHintState {
  if (typeof localStorage === 'undefined') return defaultState();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return defaultState();
    const parsed = JSON.parse(raw);
    return isValidStoredState(parsed)
      ? hydrateFromPersisted(parsed)
      : defaultState();
  } catch {
    return defaultState();
  }
}

let state: OnboardingHintState = readFromStorage();
const listeners = new Set<() => void>();
let tourLightsTimer: ReturnType<typeof setTimeout> | null = null;

function clearTourLightsTimer(): void {
  if (tourLightsTimer !== null) {
    clearTimeout(tourLightsTimer);
    tourLightsTimer = null;
  }
}

function scheduleTourLightsUnlock(): void {
  clearTourLightsTimer();
  tourLightsTimer = setTimeout(() => {
    tourLightsTimer = null;
    if (!state.tourLightsUnlocked) {
      state = { ...state, tourLightsUnlocked: true };
      notifyListeners();
    }
  }, TOUR_LIGHTS_DELAY_MS);
}

function notifyListeners(): void {
  for (const listener of listeners) listener();
}

/**
 * Persists intro and node seen flags only. Session fields are never written to storage.
 */
function persist(): void {
  const payload: PersistedOnboardingState = {
    introSeen: state.introSeen,
    seenNodeIds: state.seenNodeIds,
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    return;
  }
}

/**
 * Drops the persisted state. A failing removal — quota, or a Teams Tab's partitioned storage — is
 * swallowed, so callers can always continue to their in-memory reset.
 */
function removeFromStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    return;
  }
}

function getSnapshot(): OnboardingHintState {
  return state;
}

/** Whether a given hint-target node id has already been shown. */
export function isNodeSeen(id: string): boolean {
  return state.seenNodeIds.includes(id);
}

/** Whether beacon gating is currently unlocked (soft-halos delay after the intro closes). */
export function isTourLightsUnlocked(): boolean {
  return state.tourLightsUnlocked;
}

/** Marks a single node id as seen. No-op if it is already marked. */
export function markNodeSeen(id: string): void {
  if (state.seenNodeIds.includes(id)) return;
  state = { ...state, seenNodeIds: [...state.seenNodeIds, id] };
  persist();
  notifyListeners();
}

/**
 * Marks the intro dialog as seen, so it never comes back. Only a deliberate finish counts —
 * Get started or Skip intro. No-op if it is already marked.
 * Schedules tour-light unlock after {@link TOUR_LIGHTS_DELAY_MS}; optional prefill path activates Send nudge.
 */
export function markIntroSeen(options?: {
  withPrefill?: boolean;
  path?: string;
}): void {
  if (state.introSeen) return;
  const withPrefill = Boolean(options?.withPrefill);
  state = {
    ...state,
    introSeen: true,
    tourLightsUnlocked: false,
    sendNudgeActive: withPrefill,
    sendNudgePath: withPrefill ? (options?.path ?? null) : null,
  };
  persist();
  notifyListeners();
  scheduleTourLightsUnlock();
}

/**
 * Closes the intro modal for this session only — the X, Esc, or a backdrop click. Nothing is
 * written to storage, so a reload shows the intro again; the tour lights still unlock as if the
 * intro had been finished, so the page behind it is not left dark. No-op once the intro is seen.
 */
export function dismissIntro(): void {
  if (state.introSeen || state.introDismissed) return;
  state = { ...state, introDismissed: true, tourLightsUnlocked: false };
  notifyListeners();
  scheduleTourLightsUnlock();
}

/** Clears the Send-button nudge (send, leave route, or dismiss). */
export function clearSendNudge(): void {
  if (!state.sendNudgeActive && state.sendNudgePath === null) return;
  state = { ...state, sendNudgeActive: false, sendNudgePath: null };
  notifyListeners();
}

/** Whether the post-intro Send nudge is active (session-only). */
export function isSendNudgeActive(): boolean {
  return state.sendNudgeActive;
}

/** Routes that mount the composer. The Send nudge is meaningful only on these routes. */
export function isComposerRoute(pathname: string): boolean {
  return (
    pathname === '/' || pathname === '/home' || pathname.startsWith('/chats')
  );
}

/**
 * Drops the Send nudge when the user leaves a route that mounts the composer.
 * Same-route remounts (React Strict Mode) do not clear it, and navigating to
 * another composer route keeps the nudge alive so it can still pulse the Send
 * button when the user lands on chat.
 */
export function clearSendNudgeIfRouteChanged(pathname: string): void {
  if (!state.sendNudgeActive || state.sendNudgePath === null) return;
  if (isComposerRoute(pathname)) return;
  clearSendNudge();
}

/** Marks every id in the given list as seen — used by the "skip all hints" action. */
export function skipAllHints(ids: string[]): void {
  const merged = new Set(state.seenNodeIds);
  for (const id of ids) merged.add(id);
  state = { ...state, seenNodeIds: [...merged] };
  persist();
  notifyListeners();
}

/**
 * Opens the dialog for a beacon hint target and marks it seen. Marking seen and opening the
 * dialog happen together because a beacon target's dialog is reached only through the gate in
 * `useHintTarget` — there is no other way to see one without it becoming seen. No-op if this
 * id's dialog is already the active one.
 */
export function openHintDialog(id: string): void {
  markNodeSeen(id);
  if (state.activeDialogHintId === id) return;
  state = { ...state, activeDialogHintId: id };
  notifyListeners();
}

/** Closes whichever beacon hint dialog is open. No-op if none is open. */
export function closeHintDialog(): void {
  if (state.activeDialogHintId === null) return;
  state = { ...state, activeDialogHintId: null };
  notifyListeners();
}

/**
 * Clears all onboarding state — every seen node and the intro flag — in memory and in storage. A
 * failing storage removal is swallowed; the in-memory reset still stands.
 */
export function resetOnboarding(): void {
  clearTourLightsTimer();
  state = defaultState();
  removeFromStorage();
  notifyListeners();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

/** React hook — re-renders when the onboarding state changes (a node or the intro gets marked seen, or a reset happens). */
export function useHintState(): OnboardingHintState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Test-only helper — re-reads storage and drops subscribers so tests can reset the module singleton. */
export function _resetHintStoreForTests(): void {
  clearTourLightsTimer();
  state = readFromStorage();
  listeners.clear();
}
